import { CompanyNoveltiesType } from '../../../types'

const TRIGGER_CODE = 672
const CANCELLED_STATUS = 2

export const PRIORITY_RULES: Record<number, { priority: number; dependsOnTrigger: boolean }> = {
  672: { priority: 1, dependsOnTrigger: false },
  630: { priority: 2, dependsOnTrigger: true },
  671: { priority: 3, dependsOnTrigger: true },
  922: { priority: 4, dependsOnTrigger: true },
}

/**
 * Mensajes informativos que se muestran debajo del campo "Prioridad" según el código
 * de interfaz seleccionado, para advertir requisitos o consideraciones de procesamiento.
 */
export const TYPE_NOTICE_MESSAGES: Record<number, string> = {
  2016: 'Si hubo ingresos, esperar a que se ejecuten las interfaces 672 y 630.',
  630: 'Para el correcto procesamiento de estas novedades, es requisito que el empleado haya sido dado de alta previamente mediante la macro 672.',
}

/**
 * Obtiene el código de interfaz VISMA (672, 630, etc.) a partir del ID del tipo de novedad seleccionado.
 * @param noveltyTypeId - ID del tipo de novedad seleccionado en el formulario.
 * @param noveltyTypes - Lista de tipos de novedad de la empresa (con `novelty_type.code`).
 * @returns El código numérico de la interfaz, o `null` si no se encuentra.
 */
export function getInterfaceCode(noveltyTypeId: string, noveltyTypes: any[]): number | null {
  const found = noveltyTypes.find((t: any) => t.id === noveltyTypeId)
  return found?.novelty_type?.code ?? null
}

/**
 * Calcula la prioridad sugerida según el código de interfaz y la existencia de una
 * interfaz 672 (trigger) en el periodo.
 *
 * Reglas:
 * - 672: siempre prioridad 1.
 * - 630, 671, 922: prioridad 2, 3, 4 respectivamente solo si existe una 672 en el periodo.
 * - Cualquier otra interfaz: sin auto-asignación.
 *
 * @param interfaceCode - Código de interfaz VISMA de la novedad seleccionada.
 * @param hasTriggerInPeriod - Indica si existe una novedad con interfaz 672 en el periodo.
 * @returns La prioridad sugerida, o `null` si no aplica auto-asignación.
 */
export function computeSuggestedPriority(
  interfaceCode: number | null,
  hasTriggerInPeriod: boolean,
): number | null {
  if (interfaceCode === null) return null

  // Busca si el código de interfaz tiene una regla de prioridad definida (672, 630, 671, 922)
  const rule = PRIORITY_RULES[interfaceCode]
  if (!rule) return null

  // Si no depende del trigger (672), asigna siempre. Si depende, solo si existe 672 en el periodo.
  if (!rule.dependsOnTrigger) return rule.priority
  return hasTriggerInPeriod ? rule.priority : null
}

// Tipos de novedad que no se identifican por su número en el nombre del
// archivo, sino por una palabra clave (legacy Ausencias/Vacaciones, ver
// ProcessMassive/RunNovelty en el backend).
const FILENAME_CODE_EXCEPTIONS: Record<number, string> = {
  2: 'ausenciasvp',
  6: 'vacacionesvp',
}

/**
 * Valida que el nombre del archivo generado por la macro del cliente
 * corresponda al código de tipo de novedad seleccionado. Misma lógica que
 * `filenameMatchesNoveltyCode` en pkg/domains/novelty.go (backend).
 * @param fileName - Nombre del archivo adjuntado.
 * @param code - Código de interfaz del tipo de novedad seleccionado, o `null` si aún no se resolvió.
 * @returns `true` si el nombre coincide con el tipo (o si no hay código para comparar).
 */
export function validateNoveltyFileName(fileName: string, code: number | null): boolean {
  if (code === null) return true

  const exceptionToken = FILENAME_CODE_EXCEPTIONS[code]
  if (exceptionToken) {
    return fileName.toLowerCase().includes(exceptionToken)
  }

  const boundaryRegex = new RegExp(`(^|\\D)${code}(\\D|$)`)
  return boundaryRegex.test(fileName)
}

/**
 * Determina si existe al menos una novedad con interfaz 672 (trigger) en el periodo.
 * Cruza las novedades del periodo con los tipos de novedad de la empresa para verificar el código.
 * Ignora novedades en estado cancelado (status 2), ya que no se consideran válidas.
 * @param periodNovelties - Novedades existentes en el periodo seleccionado.
 * @param noveltyTypes - Lista de tipos de novedad de la empresa (con `novelty_type.code`).
 * @returns `true` si existe una novedad con código 672 no cancelada en el periodo.
 */
export function hasTriggerNovelty(
  periodNovelties: CompanyNoveltiesType[],
  noveltyTypes: any[],
): boolean {
  return periodNovelties.some(novelty => {
    if (novelty.status === CANCELLED_STATUS) return false
    const type = noveltyTypes.find((t: any) => t.id === novelty.novelty_type_id)
    return type?.novelty_type?.code === TRIGGER_CODE
  })
}
